import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// Clippy — appears after 30 seconds to give sarcastic IT advice.
// Only resets idle on clicks/keydown (NOT mousemove) so users actually see him.

const CLIPPY_MESSAGES = [
  "It looks like you're trying to run a business without IT support.\n\nBold strategy. Let's see how that works out.\n\n📞 0118 359 2220\ndeadsimplecomputing.co.uk",
  "Hi! I see you haven't updated Windows since 2004.\n\nThat's... actually impressive commitment to ignoring security.\n\nMaybe let the professionals handle it?\n📞 Dead Simple Computing",
  "It looks like you're writing an important email.\n\nWould you like me to:\n• Accidentally reply all?\n• Attach the wrong file?\n• CC your competitor?\n\nOr call someone competent:\n📞 0118 359 2220",
  "I notice your antivirus expired 19 years ago.\n\nDon't worry. The 47 trojans living in your system tray are keeping each other company.\n\n— Dead Simple Computing\n📞 0118 359 2220",
  "It looks like you're trying to fix your own computer.\n\nHistorically, this goes badly.\n\nLast person who tried this ended up with a PC that could only display Comic Sans.\n\n📞 0118 359 2220",
  "Hey! I found your passwords.txt file on the desktop.\n\nYour WiFi password is 'password123'.\n\nI've taken the liberty of emailing it to everyone in your contacts.\n\n...Kidding. But seriously, call DSC.\n📞 0118 359 2220",
  "Fun fact: Your computer has more viruses than a Year 2 classroom in February.\n\nDead Simple Computing can fix that.\n\nOr you can keep ignoring me. Your call.\n📞 0118 359 2220",
  "I see 847 Windows Updates are pending.\n\nAt this rate, you'll finish installing them by the heat death of the universe.\n\nOr you could call Dead Simple Computing and they'll sort it in about an hour.\n📞 0118 359 2220",
  "Your firewall is off. Your antivirus is expired. You're running Internet Explorer.\n\nThis is basically the IT equivalent of leaving your front door open with a sign that says 'FREE STUFF'.\n\n📞 0118 359 2220",
  "BREAKING NEWS: Local business owner discovers their IT infrastructure is held together with hopes, dreams, and a copy of Norton from 2003.\n\nMore at 11.\n\n📞 Dead Simple Computing\n0118 359 2220",
];

function Clippy({ idleTimeout = 30000 }) {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const lastActivity = useRef(Date.now());

  useEffect(() => {
    if (dismissed) return;

    function resetTimer() {
      lastActivity.current = Date.now();
    }

    function checkIdle() {
      if (Date.now() - lastActivity.current >= idleTimeout && !visible && !dismissed) {
        const msg = CLIPPY_MESSAGES[Math.floor(Math.random() * CLIPPY_MESSAGES.length)];
        setMessage(msg);
        setVisible(true);
      }
    }

    // Only reset on clicks and keyboard — NOT mousemove
    // This way Clippy appears even while users are browsing
    window.addEventListener('mousedown', resetTimer);
    window.addEventListener('keydown', resetTimer);

    const timer = setInterval(checkIdle, 3000);

    return () => {
      window.removeEventListener('mousedown', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      clearInterval(timer);
    };
  }, [visible, dismissed, idleTimeout]);

  function handleDismiss() {
    setVisible(false);
    setDismissed(true);
    // Show again after 2 minutes
    setTimeout(() => setDismissed(false), 120000);
  }

  if (!visible) return null;

  return (
    <Container>
      <SpeechBubble>
        <BubbleText>{message}</BubbleText>
        <BubbleButtons>
          <BubbleButton onClick={handleDismiss}>Got it!</BubbleButton>
          <BubbleButton onClick={handleDismiss}>Sod off Clippy</BubbleButton>
        </BubbleButtons>
        <BubbleTail />
      </SpeechBubble>
      <ClippyCharacter onClick={handleDismiss}>📎</ClippyCharacter>
    </Container>
  );
}

const bounceIn = keyframes`
  0% { transform: scale(0) translateY(50px); opacity: 0; }
  60% { transform: scale(1.1) translateY(-10px); opacity: 1; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;

const wobble = keyframes`
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
`;

const Container = styled.div`
  position: fixed;
  bottom: 50px;
  right: 30px;
  z-index: 99999;
  animation: ${bounceIn} 0.5s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SpeechBubble = styled.div`
  background: #FFFFCC;
  border: 2px solid #333;
  border-radius: 12px;
  padding: 14px 16px;
  max-width: 280px;
  margin-bottom: 8px;
  position: relative;
  box-shadow: 2px 2px 5px rgba(0,0,0,0.3);
`;

const BubbleText = styled.div`
  font-family: 'Segoe UI', Tahoma, sans-serif;
  font-size: 12px;
  line-height: 1.5;
  color: #333;
  white-space: pre-wrap;
  margin-bottom: 10px;
`;

const BubbleButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const BubbleButton = styled.button`
  background: linear-gradient(180deg, #fff 0%, #e0e0e0 100%);
  border: 1px solid #999;
  border-radius: 3px;
  padding: 3px 12px;
  font-size: 11px;
  font-family: Tahoma, sans-serif;
  cursor: pointer;
  &:hover {
    background: linear-gradient(180deg, #e8e8e8 0%, #d0d0d0 100%);
  }
  &:active {
    background: #ccc;
  }
`;

const BubbleTail = styled.div`
  position: absolute;
  bottom: -10px;
  right: 30px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid #FFFFCC;
  &::before {
    content: '';
    position: absolute;
    bottom: 2px;
    left: -12px;
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-top: 12px solid #333;
    z-index: -1;
  }
`;

const ClippyCharacter = styled.div`
  font-size: 56px;
  cursor: pointer;
  animation: ${wobble} 2s ease-in-out infinite;
  filter: drop-shadow(2px 2px 3px rgba(0,0,0,0.3));
  line-height: 1;
`;

export default Clippy;
